import type { ApiEnvelope } from '../types';
import type { ApiModuleDeps } from './internal/request';
import type {
    ApiCredits,
    CheckoutSession,
    CreditsBalanceResponse,
    CreditsDashboardResponse,
    InfraRateCardResponse,
    PaymentProduct,
    ProjectUsageItem,
    SubscriptionBillingCycleApi,
    SubscriptionPlanTypeApi,
    SubscriptionPlansApiResponse,
    SubscriptionStatusApiResponse,
    TransferCreditsRequest,
    TransferCreditsResponse,
    UpdateAutoTransferConfigRequest,
    UsageHistoryQueryParams,
    UsageHistoryResponse,
} from '../types';
import { createPublicResourceResolver } from './resources/public-url-resolver';

type NumericApiEnvelope<T> = Omit<ApiEnvelope<T>, 'code'> & { code: number };

export function createBillingApi({ authHttp, noAuthHttp }: ApiModuleDeps) {
    const { getDownloadPublicResourcePresignedURLs } = createPublicResourceResolver(noAuthHttp);
    return {
        async getWorkspaceCredits(workspace_id: number): Promise<ApiEnvelope<ApiCredits>> {
            const response = await authHttp.get<ApiEnvelope<ApiCredits>>(
                `/workspaces/${workspace_id}/credits`
            );
            return response;
        },

        async getCreditsBalance(
            workspace_id: number
        ): Promise<ApiEnvelope<CreditsBalanceResponse>> {
            return authHttp.get<ApiEnvelope<CreditsBalanceResponse>>(
                `/workspaces/${workspace_id}/credits`
            );
        },

        async createCheckout(
            workspaceId: number,
            request: {
                price_id: string;
                quantity: number;
                success_url: string;
                cancel_url: string;
            }
        ): Promise<ApiEnvelope<{ session: CheckoutSession }>> {
            const response = await authHttp.post<ApiEnvelope<{ session: CheckoutSession }>>(
                `/workspaces/${workspaceId}/payment/checkout`,
                request
            );
            return response;
        },

        async claimCredits(code: string): Promise<NumericApiEnvelope<Record<string, never>>> {
            const response = await authHttp.post<NumericApiEnvelope<Record<string, never>>>(
                `/referral/claim`,
                null,
                {
                    query: {
                        code,
                    },
                }
            );
            return response;
        },

        async getCreditsDashboard(
            workspace_id: number
        ): Promise<ApiEnvelope<CreditsDashboardResponse>> {
            const response = await authHttp.get<ApiEnvelope<CreditsDashboardResponse>>(
                `/workspaces/${workspace_id}/credits/dashboard`
            );
            const result = response;

            // 处理 usage_history.projects 中的 cover 字段转换为 cover_url
            if (result.data?.usage_history?.projects) {
                const projects = result.data.usage_history.projects;
                const projectsWithCover = projects.filter(
                    (project: ProjectUsageItem) => project.cover
                );
                if (projectsWithCover.length > 0) {
                    const coverResources = projectsWithCover.flatMap((project: ProjectUsageItem) =>
                        project.cover ? [project.cover] : []
                    );
                    const urlsResp = await getDownloadPublicResourcePresignedURLs(coverResources);
                    if (urlsResp.code === 0) {
                        const urlsMap = urlsResp.data.urls;
                        projects.forEach((project: ProjectUsageItem) => {
                            if (project.cover && urlsMap[project.cover]) {
                                project.cover_url = urlsMap[project.cover];
                            }
                        });
                    }
                }
            }

            return result;
        },

        async transferCredits(
            workspace_id: number,
            request: TransferCreditsRequest
        ): Promise<ApiEnvelope<TransferCreditsResponse>> {
            const response = await authHttp.post<ApiEnvelope<TransferCreditsResponse>>(
                `/workspaces/${workspace_id}/credits/transfer`,
                request
            );
            return response;
        },

        async paymentSuccess(workspace_id: number): Promise<ApiEnvelope<Record<string, never>>> {
            const response = await authHttp.post<ApiEnvelope<Record<string, never>>>(
                `/workspaces/${workspace_id}/credits/payment-success`,
                {}
            );
            return response;
        },

        async getCreditsUsageHistory(
            workspace_id: number,
            params: UsageHistoryQueryParams = {}
        ): Promise<ApiEnvelope<UsageHistoryResponse>> {
            const queryParams = new URLSearchParams();
            if (params.usage_type) queryParams.set('usage_type', params.usage_type);
            if (params.page) queryParams.set('page', String(params.page));
            if (params.page_size) queryParams.set('page_size', String(params.page_size));
            if (params.sort_by) queryParams.set('sort_by', params.sort_by);
            if (params.sort_order) queryParams.set('sort_order', params.sort_order);
            if (params.product) queryParams.set('product', params.product);
            if (params.resource_type) queryParams.set('resource_type', params.resource_type);
            if (params.billing_status) queryParams.set('billing_status', params.billing_status);
            if (params.period_start) queryParams.set('period_start', params.period_start);
            if (params.period_end) queryParams.set('period_end', params.period_end);
            if (params.instance_id) queryParams.set('instance_id', String(params.instance_id));

            const queryString = queryParams.toString();
            const url = `/workspaces/${workspace_id}/credits/usage-history${queryString ? `?${queryString}` : ''}`;
            const response = await authHttp.get<ApiEnvelope<UsageHistoryResponse>>(url);
            const result = response;

            // 处理 cover 字段转换为 cover_url
            if (result.data && 'projects' in result.data && result.data.projects) {
                const projects = result.data.projects;
                const projectsWithCover = projects.filter(
                    (project: ProjectUsageItem) => project.cover
                );
                if (projectsWithCover.length > 0) {
                    const coverResources = projectsWithCover.flatMap((project: ProjectUsageItem) =>
                        project.cover ? [project.cover] : []
                    );
                    const urlsResp = await getDownloadPublicResourcePresignedURLs(coverResources);
                    if (urlsResp.code === 0) {
                        const urlsMap = urlsResp.data.urls;
                        projects.forEach((project: ProjectUsageItem) => {
                            if (project.cover && urlsMap[project.cover]) {
                                project.cover_url = urlsMap[project.cover];
                            }
                        });
                    }
                }
            }

            return result;
        },

        async getCreditsInfraRateCard(
            workspace_id: number
        ): Promise<ApiEnvelope<InfraRateCardResponse>> {
            return authHttp.get<ApiEnvelope<InfraRateCardResponse>>(
                `/workspaces/${workspace_id}/credits/infra/rate-card`
            );
        },

        async getSubscriptionPlans(): Promise<ApiEnvelope<SubscriptionPlansApiResponse>> {
            const response =
                await noAuthHttp.get<ApiEnvelope<SubscriptionPlansApiResponse>>(
                    `/subscription/plans`
                );
            return response;
        },

        async getSubscriptionStatus(
            workspaceId: number
        ): Promise<ApiEnvelope<SubscriptionStatusApiResponse>> {
            const response = await authHttp.get<ApiEnvelope<SubscriptionStatusApiResponse>>(
                `/workspaces/${workspaceId}/subscription/status`
            );
            return response;
        },

        async createSubscriptionCheckout(
            workspaceId: number,
            request: {
                price_id: string;
                plan_type: SubscriptionPlanTypeApi;
                billing_cycle: SubscriptionBillingCycleApi;
                success_url: string;
                cancel_url: string;
            }
        ): Promise<ApiEnvelope<{ session: CheckoutSession; workspace_id: string }>> {
            const response = await authHttp.post<
                ApiEnvelope<{ session: CheckoutSession; workspace_id: string }>
            >(`/workspaces/${workspaceId}/subscription/checkout`, request);
            return response;
        },

        async createWorkspaceWithSubscription(request: {
            workspace_name: string;
            price_id: string;
            plan_type: SubscriptionPlanTypeApi;
            billing_cycle: SubscriptionBillingCycleApi;
            success_url: string;
            cancel_url: string;
        }): Promise<ApiEnvelope<{ session: CheckoutSession; workspace_id: string }>> {
            const response = await authHttp.post<
                ApiEnvelope<{ session: CheckoutSession; workspace_id: string }>
            >('/workspaces/subscribe', request);
            return response;
        },

        async notifySubscriptionSuccess(
            workspaceId: number
        ): Promise<ApiEnvelope<SubscriptionStatusApiResponse>> {
            const response = await authHttp.post<ApiEnvelope<SubscriptionStatusApiResponse>>(
                `/workspaces/${workspaceId}/subscription/notify-success`
            );
            return response;
        },

        listProducts(): Promise<ApiEnvelope<{ products: PaymentProduct[] }>> {
            return authHttp.get('/payment/products');
        },

        updateAutoTransferConfig(
            workspaceId: number,
            config: UpdateAutoTransferConfigRequest
        ): Promise<ApiEnvelope<Record<string, never>>> {
            return authHttp.put(
                '/workspaces/' + workspaceId + '/credits/auto-transfer-config',
                config
            );
        },
    };
}
